'use client';

import { UTCDate } from '@date-fns/utc';
import { intervalToDuration } from 'date-fns';
import { useEffect, useMemo, useState } from 'react';

type UseCountdownProps = {
  /**
   * The target date for the countdown. This could be a Date object,
   * or a string in ISO 8601 format (e.g. "2025-01-31T00:00:00Z").
   */
  targetDate: string | Date;

  /**
   * Interval in milliseconds at which the countdown updates.
   * Defaults to 1 second (1000 ms).
   */
  intervalMs?: number;

  /**
   * Optional callback to handle "expired" case (if targetDate is in the past).
   * If provided, the countdown stops once the date has passed.
   */
  onExpire?: () => void;
};

type UseCountdownReturn = {
  /**
   * The formatted time remaining string (e.g., "5d 3h 22m 15s")
   */
  timeRemaining: string | null;

  /**
   * Whether the countdown has expired (target date has passed)
   */
  isExpired: boolean;

  /**
   * The duration object from date-fns with individual time units
   */
  duration: {
    days: number;
    hours: number;
    minutes: number;
    seconds: number;
  };
};

export const useCountdown = ({
  targetDate,
  intervalMs = 1000,
  onExpire,
}: UseCountdownProps): UseCountdownReturn => {
  const [timeRemaining, setTimeRemaining] = useState<string | null>(null);
  const [duration, setDuration] = useState({
    days: 0,
    hours: 0,
    minutes: 0,
    seconds: 0,
  });

  // Convert the targetDate to a UTCDate (or standard Date) only once
  const matchDay = useMemo(() => {
    // If you want a purely UTC-based time, ensure you have @date-fns/utc installed:
    return new UTCDate(targetDate);

    // Otherwise, using parseISO from date-fns is often sufficient:
    // return typeof targetDate === 'string' ? parseISO(targetDate) : targetDate;
  }, [targetDate]);

  useEffect(() => {
    function updateCountdown() {
      const now = new UTCDate(); // If you are strictly dealing with UTC

      if (matchDay < now) {
        setTimeRemaining('0d 0h 0m 0s');
        setDuration({ days: 0, hours: 0, minutes: 0, seconds: 0 });
        if (onExpire) {
          onExpire();
        }
        return;
      }

      const durationObj = intervalToDuration({ start: now, end: matchDay });
      const { days = 0, hours = 0, minutes = 0, seconds = 0 } = durationObj;

      setDuration({ days, hours, minutes, seconds });
      setTimeRemaining(`${days}d ${hours}h ${minutes}m ${seconds}s`);
    }

    // Initialize the countdown immediately
    updateCountdown();

    const intervalId = setInterval(updateCountdown, intervalMs);

    return () => {
      clearInterval(intervalId);
    };
  }, [matchDay, intervalMs, onExpire]);

  const isExpired = timeRemaining === '0d 0h 0m 0s';

  return {
    timeRemaining,
    isExpired,
    duration,
  };
};
